import type { FheChain } from "../chains/types";
import type {
  GenericLogger,
  UpdateCsrfResponseData,
  WorkerEnv,
  WorkerRequest,
  WorkerRequestType,
  WorkerResponse,
} from "./worker.types";
import { BaseWorkerClient } from "./worker.base-client";
import { getBrowserExtensionRuntime } from "./browser-extension";
import { default as workerCode, filename as workerFilename } from "./relayer-sdk.worker.ts?iife";

/** Configuration for the worker client */
export interface WorkerClientConfig {
  cdnUrl: string;
  chains: FheChain[];
  csrfToken: string;
  /** Expected SHA-384 hex digest of the CDN bundle for integrity verification. */
  integrity?: string;
  /** SDK-wide logger for tracing worker request lifecycle. */
  logger: GenericLogger;
  /** Number of WASM threads for parallel FHE operations (passed to `initSDK({ thread })`). */
  thread?: number;
}

/**
 * Client for communicating with the RelayerSDK Web Worker.
 * Provides a promise-based API for FHE operations.
 */
export class RelayerWorkerClient extends BaseWorkerClient<Worker, WorkerClientConfig> {
  protected readonly env: WorkerEnv = "web";

  constructor(config: WorkerClientConfig) {
    super(config, config.logger);
  }

  protected createWorker(): Worker {
    const runtime = getBrowserExtensionRuntime();
    if (runtime) {
      return new Worker(runtime.getURL(workerFilename));
    }
    const blobUrl = URL.createObjectURL(new Blob([workerCode], { type: "application/javascript" }));
    try {
      return new Worker(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }

  protected wireEvents(worker: Worker): void {
    worker.onmessage = (event: MessageEvent<WorkerResponse<unknown>>) =>
      this.handleResponse(event.data);
    worker.onerror = (event: ErrorEvent) => this.handleWorkerError(event.message);
    worker.onmessageerror = () => this.handleWorkerMessageError();
  }

  protected postMessage(worker: Worker, request: WorkerRequest): void {
    worker.postMessage(request);
  }

  protected terminateWorker(worker: Worker): void {
    worker.terminate();
  }

  protected generateRequestId(): string {
    return crypto.randomUUID();
  }

  protected getInitPayload(): {
    type: WorkerRequestType;
    payload: WorkerRequest["payload"];
  } {
    // Explicitly construct the payload from serializable fields only.
    // Functions (e.g. `logger`) cannot be cloned by the structured clone
    // algorithm used by `worker.postMessage()`.
    const { cdnUrl, chains, csrfToken, integrity, thread } = this.config;
    return {
      type: "INIT",
      payload: { env: "web" as const, cdnUrl, chains, csrfToken, integrity, thread },
    };
  }

  /**
   * Update the CSRF token in the worker.
   * Call this before making authenticated requests to ensure the token is fresh.
   */
  async updateCsrf(csrfToken: string): Promise<void> {
    await this.sendRequest<UpdateCsrfResponseData>("UPDATE_CSRF", {
      csrfToken,
    });
  }
}
